import {
  Button,
  Descriptions,
  Edit,
  EmptyState,
  FormCard,
  NotStartedIcon,
  PageHeader,
} from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { ButtonsContainer, PageWrapper, SectionHeader } from '../../components'
import { getIsContractPreferencesEmpty, labelStyle } from '../../utils'
import { styled } from 'styled-components'
import AvailabilityPreferenceTable from './components/AvailabilityPreferenceTable'
import { useRouter } from 'hooks/useRouter'
import useUser from 'hooks/useUser/useUser'
import { HomeDto, User } from '@lmc/ts-common'
import dayjs from 'dayjs'
import { getContractItems, getOtherItems } from '../utils'
import { useSelector } from 'react-redux'
import { TIME_LAST_DATE_FORMAT } from 'screens/Rostering/utils.datetime'
import useHomeType from 'hooks/useHomeType'

export const ContractAndPreferencesScreen = () => {
  const { t } = useTranslation()
  const { location, navigate, params } = useRouter()
  const { user } = useUser({ id: params?.id, includes: ['rostering'] })
  const userWithWorkProfile = user as unknown as User
  const { user: userUpdatedBy } = useUser({
    id: user?.workProfile?.updatedBy as unknown as string,
  })
  const { hasRota } = useHomeType()

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home,
  )

  const hasEmptyContractPreferencesEmpty = getIsContractPreferencesEmpty(user)

  const headerSubtitle = userUpdatedBy
    ? t('LAST_UPDATED_DATE_BY', {
        date: dayjs(user?.workProfile?.editedOn).format(TIME_LAST_DATE_FORMAT),
        by: userUpdatedBy?.name ? '- By' : '',
        user: `${userUpdatedBy?.name?.first} ${userUpdatedBy?.name?.last}`,
      })
    : undefined

  return (
    <>
      <PageHeader
        title={hasRota ? t('CONTRACT_AND_PREFERENCES') : t('CONTRACT')}
        sticky
        subtitle={headerSubtitle}
        data-testid="contract-and-preferences-header"
      >
        <ButtonsContainer data-testid="contract-and-preferences-actions">
          <Button
            variant="primary"
            onClick={() => navigate(`${location.pathname}/edit`)}
            data-cy="edit-contract-btn"
            data-testid="contract-and-preferences-edit-button"
          >
            <Edit />
            {t('EDIT')}
          </Button>
        </ButtonsContainer>
      </PageHeader>
      <PageWrapper data-testid="contract-and-preferences-content">
        {!hasEmptyContractPreferencesEmpty && (
          <>
            <SectionHeader
              title={t('INFORMATION')}
              data-testid="contract-and-preferences-information-section"
            />
            <CardsContainer data-testid="contract-and-preferences-cards-container">
              <InfoCard
                title={t('CONTRACT_INFORMATION')}
                data-testid="contract-and-preferences-contract-card"
              >
                <Descriptions
                  bordered
                  column={1}
                  labelStyle={labelStyle}
                  items={getContractItems(
                    userWithWorkProfile.workProfile,
                    t,
                    home,
                  )}
                  data-testid="contract-and-preferences-contract-descriptions"
                />
              </InfoCard>
              {hasRota &&
                !!getOtherItems(userWithWorkProfile.workProfile, t)?.length && (
                  <InfoCard
                    title={t('OTHER')}
                    data-testid="contract-and-preferences-other-card"
                  >
                    <Descriptions
                      bordered
                      column={1}
                      labelStyle={labelStyle}
                      items={getOtherItems(userWithWorkProfile.workProfile, t)}
                      data-testid="contract-and-preferences-other-descriptions"
                    />
                  </InfoCard>
                )}
            </CardsContainer>
            {hasRota && (
              <FormCard
                title={t('AVAILABILITY_PREFERENCE')}
                subtitle={t('AVAILABILITY_PREFERENCE_DESC')}
                data-testid="contract-and-preferences-availability-card"
              >
                <AvailabilityPreferenceTable
                  availability={user?.workProfile?.availability}
                  data-testid="contract-and-preferences-availability-table"
                />
              </FormCard>
            )}
          </>
        )}
        {hasEmptyContractPreferencesEmpty && (
          <EmptyState
            title={t('EMPTY_STATE_NOT_STARTED_TITLE')}
            icon={<NotStartedIcon />}
            description={t('EMPTY_STATE_NOT_STARTED_CONTRACT')}
            data-testid="contract-and-preferences-empty-state"
          />
        )}
      </PageWrapper>
    </>
  )
}

const CardsContainer = styled.div`
  display: flex;
  gap: 24px;
`

const InfoCard = styled(FormCard)`
  flex: 1;
  justify-content: flex-start;
`
